import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Palette des statuts, résolue en deux étages :
 *   1. le DÉFAUT livré ci-dessous (repris du prototype) ;
 *   2. la SURCHARGE du joueur (`player_display_preference`, clés `statut.<code>.bg` / `.fg`).
 *
 * Fond ET écriture comptent : deux statuts peuvent partager le même fond et ne se
 * distinguer que par la couleur du texte — c'est le cas dans le tableau du jeu.
 *
 * Si l'API n'est pas disponible (migration non appliquée, hors ligne), on reste
 * simplement sur les défauts : la palette ne disparaît jamais.
 */

export interface StatusStyle {
  bg: string;
  fg: string;
  label: string;
}

/**
 * Défauts repris du prototype (`STATUT_PALETTE`, data-buildings.js), alignés sur
 * les codes réels des statuts dispatch (`server/gameplay/dispatchStatuses.ts`).
 * Le fond ET l'écriture comptent : plusieurs statuts partagent un fond et ne se
 * distinguent que par la couleur du texte (ex. Transport / Arrivé transport).
 */
export const DEFAULT_STATUS_PALETTE: Record<string, StatusStyle> = {
  // Disponibilité à la base
  disponible: { bg: '#22B14C', fg: '#000000', label: 'Disponible' },
  materiel: { bg: '#12703A', fg: '#000000', label: 'Disponible matériel' },
  en_manoeuvre: { bg: '#22B14C', fg: '#F7941D', label: 'En manœuvre' },
  indisponible: { bg: '#3A3A3A', fg: '#FFFFFF', label: 'Indisponible' },
  formation: { bg: '#1B3F8B', fg: '#FF0000', label: 'En formation' },
  // Alerte / départ
  en_alerte: { bg: '#EDE500', fg: '#000000', label: 'En alerte' },
  prealerte: { bg: '#EDE500', fg: '#F7941D', label: 'Préalerte' },
  en_route: { bg: '#F7941D', fg: '#000000', label: 'En route' },
  // Sur les lieux
  sur_les_lieux: { bg: '#F01C1C', fg: '#000000', label: 'Sur les lieux' },
  sur_place: { bg: '#F01C1C', fg: '#000000', label: 'Sur place' },
  operation_multiple: { bg: '#F01C1C', fg: '#EDE500', label: 'Opération multiple' },
  desinfection: { bg: '#0F766E', fg: '#FFFFFF', label: 'Désinfection' },
  // Noria
  noria_victimes: { bg: '#6B3FA0', fg: '#FFFFFF', label: 'Noria victimes' },
  noria_hostiles: { bg: '#6B3FA0', fg: '#F7941D', label: 'Noria hostiles' },
  noria_materiels: { bg: '#6B3FA0', fg: '#00B0F0', label: 'Noria matériels' },
  // Transport (fusionné + détaillé)
  transport: { bg: '#F01C1C', fg: '#FFFFFF', label: 'Transport' },
  transport_hopital: { bg: '#F01C1C', fg: '#FFFFFF', label: 'Transport hôpital' },
  transport_veterinaire: { bg: '#F01C1C', fg: '#FFFFFF', label: 'Transport vétérinaire' },
  transport_gav: { bg: '#F01C1C', fg: '#FFFFFF', label: 'Transport GAV' },
  transport_prison: { bg: '#F01C1C', fg: '#FFFFFF', label: 'Transport prison' },
  transport_remorquage: { bg: '#F01C1C', fg: '#FFFFFF', label: 'Transport remorquage' },
  // Arrivée à destination
  arrive_transport: { bg: '#F01C1C', fg: '#0000FF', label: 'Arrivé transport' },
  arrive_hopital: { bg: '#F01C1C', fg: '#0000FF', label: 'Arrivé hôpital' },
  arriver_hopital: { bg: '#F01C1C', fg: '#0000FF', label: 'Arrivé hôpital' },
  arriver_veterinaire: { bg: '#F01C1C', fg: '#0000FF', label: 'Arrivé vétérinaire' },
  arriver_gav: { bg: '#F01C1C', fg: '#0000FF', label: 'Arrivé GAV' },
  arriver_prison: { bg: '#F01C1C', fg: '#0000FF', label: 'Arrivé prison' },
  arriver_remorquage: { bg: '#F01C1C', fg: '#0000FF', label: 'Arrivé remorquage' },
  // Indisponibilités / statuts coarse véhicule
  en_mission: { bg: '#F7941D', fg: '#000000', label: 'En mission' },
  en_maintenance: { bg: '#3A3A3A', fg: '#22B14C', label: 'Maintenance' },
  panne: { bg: '#3A3A3A', fg: '#FF0000', label: 'En panne' },
};

const prefKey = (code: string, part: 'bg' | 'fg') => `statut.${code}.${part}`;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export function useStatusPalette() {
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [ready, setReady] = useState(false);
  const serverOkRef = useRef(true);
  const base = `${getApiBaseUrl(true)}/display-preferences`;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(base, { headers: authHeaders() });
        if (!res.ok) throw new Error('indisponible');
        const data = await res.json();
        if (!alive) return;
        // Table absente (migration non appliquée) : on reste sur les défauts.
        if (data?.persisted === false) {
          serverOkRef.current = false;
          return;
        }
        setOverrides(data?.preferences && typeof data.preferences === 'object' ? data.preferences : {});
      } catch {
        if (!alive) return;
        serverOkRef.current = false;
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [base]);

  /** Palette effective : défaut écrasé par la surcharge du joueur. */
  const palette = useMemo(() => {
    const out: Record<string, StatusStyle> = {};
    for (const [code, def] of Object.entries(DEFAULT_STATUS_PALETTE)) {
      out[code] = {
        label: def.label,
        bg: overrides[prefKey(code, 'bg')] || def.bg,
        fg: overrides[prefKey(code, 'fg')] || def.fg,
      };
    }
    return out;
  }, [overrides]);

  /** Enregistre une couleur. `null` = revenir au défaut (la préférence est supprimée). */
  const setColor = useCallback(
    async (code: string, part: 'bg' | 'fg', value: string | null) => {
      const key = prefKey(code, part);
      setOverrides((prev) => {
        const next = { ...prev };
        if (value == null) delete next[key];
        else next[key] = value;
        return next;
      });
      if (!serverOkRef.current) return;
      try {
        const res = await fetch(base, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ preferences: { [key]: value ?? '' } }),
        });
        if (!res.ok) serverOkRef.current = false;
      } catch {
        serverOkRef.current = false;
      }
    },
    [base],
  );

  /** Remet toute la palette aux valeurs par défaut. */
  const resetAll = useCallback(async () => {
    const keys = Object.keys(DEFAULT_STATUS_PALETTE).flatMap((c) => [prefKey(c, 'bg'), prefKey(c, 'fg')]);
    setOverrides({});
    if (!serverOkRef.current) return;
    try {
      const body: Record<string, string> = {};
      for (const k of keys) body[k] = '';
      const res = await fetch(base, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ preferences: body }),
      });
      if (!res.ok) serverOkRef.current = false;
    } catch {
      serverOkRef.current = false;
    }
  }, [base]);

  const isCustomised = useCallback(
    (code: string) => Boolean(overrides[prefKey(code, 'bg')] || overrides[prefKey(code, 'fg')]),
    [overrides],
  );

  return { palette, setColor, resetAll, isCustomised, ready, persisted: serverOkRef.current };
}
